/**
 * Home module API — Supabase queries for habits, quotes, feedback, tasks.
 * All functions use .throwOnError() per project rules.
 */

import { supabase } from '@turnifly/shared/api'
import { getLocalTodayStr } from '@turnifly/shared/utils'

// ── HABITS ──────────────────────────────────────────────────

interface MonthlyHabitsParams {
  userId: string
  year: number
  month: number
}

export async function fetchMonthlyHabits({ userId, year, month }: MonthlyHabitsParams): Promise<unknown> {
  const { data } = await supabase
    .rpc('get_monthly_habits', { p_user_id: userId, p_year: year, p_month: month })
    .throwOnError()
  return data
}

export interface ProofMedia {
  url: string
  type: 'image' | 'video'
}

export interface UpsertHabitLogParams {
  userId: string
  habitId: string
  logDate: string // ISO date string YYYY-MM-DD
  completed: boolean
  value?: number | null
  proofUrl?: string | null   // legacy single proof (backward compat)
  proofType?: string | null  // legacy
  proofUrls?: ProofMedia[]   // new: up to 3 images + 1 video
  proofText?: string | null  // new: text proof (400 chars max)
}

export async function upsertHabitLog(params: UpsertHabitLogParams): Promise<void> {
  await supabase
    .from('habit_logs')
    .upsert(
      {
        user_id: params.userId,
        habit_id: params.habitId,
        log_date: params.logDate,
        completed: params.completed,
        value: params.value ?? null,
        proof_url: params.proofUrl ?? null,
        proof_type: params.proofType ?? null,
        proof_urls: params.proofUrls ?? [],
        proof_text: params.proofText ?? null,
        completed_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,habit_id,log_date' },
    )
    .throwOnError()
}

export async function fetchHabitProfileStats(habitId: string): Promise<unknown> {
  const { data } = await supabase
    .rpc('get_habit_profile_stats', { p_habit_id: habitId })
    .throwOnError()
  return data
}

export async function deleteHabitLog(userId: string, habitId: string, logDate: string): Promise<void> {
  await supabase
    .from('habit_logs')
    .delete()
    .match({ user_id: userId, habit_id: habitId, log_date: logDate })
    .throwOnError()
}

export interface CreateHabitParams {
  userId: string
  name: string
  icon?: string
  category?: string
  description?: string
  frequency?: string
  weeklyDays?: number[]
  customInterval?: number
  startDate?: string
  endDate?: string | null
  timeMode?: string
  timeDuration?: number | null
  timeStart?: string | null
  timeEnd?: string | null
  valueRequired?: boolean
  valueTarget?: number | null
  valueUnit?: string | null
  valueComparison?: string
  proofTypes?: string[]
  penaltyText?: string | null
  reminderEnabled?: boolean
  goalId?: string | null
  challengeId?: string | null
  sortOrder?: number
}

export async function createHabit(params: CreateHabitParams): Promise<unknown> {
  const { data } = await supabase
    .from('habits')
    .insert({
      user_id: params.userId,
      name: params.name,
      icon: params.icon ?? '📌',
      category: params.category ?? null,
      description: params.description ?? null,
      frequency: params.frequency ?? 'daily',
      weekly_days: params.weeklyDays ?? null,
      custom_interval: params.customInterval ?? null,
      start_date: params.startDate ?? getLocalTodayStr(),
      end_date: params.endDate ?? null,
      time_mode: params.timeMode ?? 'anytime',
      time_duration: params.timeDuration ?? null,
      time_start: params.timeStart ?? null,
      time_end: params.timeEnd ?? null,
      value_required: params.valueRequired ?? false,
      value_target: params.valueTarget ?? null,
      value_unit: params.valueUnit ?? null,
      value_comparison: params.valueComparison ?? 'gte',
      proof_types: params.proofTypes ?? [],
      penalty_text: params.penaltyText ?? null,
      reminder_enabled: params.reminderEnabled ?? false,
      goal_id: params.goalId ?? null,
      challenge_id: params.challengeId ?? null,
      sort_order: params.sortOrder ?? 0,
    })
    .select()
    .single()
    .throwOnError()
  return data
}

/** Safe fields that client is allowed to update on a habit */
const HABIT_UPDATE_WHITELIST = new Set([
  'name', 'icon', 'category', 'description', 'frequency', 'weekly_days',
  'custom_interval', 'start_date', 'end_date', 'time_mode', 'time_duration',
  'time_start', 'time_end', 'value_required', 'value_target', 'value_unit',
  'value_comparison', 'proof_types', 'penalty_text', 'reminder_enabled',
  'reminder_on_miss', 'reminder_miss_text', 'sort_order',
])

export async function updateHabit(habitId: string, updates: Record<string, unknown>): Promise<void> {
  const safeUpdates: Record<string, unknown> = { updated_at: new Date().toISOString() }
  for (const key of Object.keys(updates)) {
    if (HABIT_UPDATE_WHITELIST.has(key)) {
      safeUpdates[key] = updates[key]
    }
  }

  await supabase
    .from('habits')
    .update(safeUpdates)
    .eq('id', habitId)
    .throwOnError()
}

export async function deleteHabit(habitId: string): Promise<void> {
  await supabase
    .from('habits')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', habitId)
    .throwOnError()
}

// ── QUOTES ──────────────────────────────────────────────────

export async function fetchTodayQuote(userId: string): Promise<unknown> {
  const { data } = await supabase
    .rpc('get_today_quote', { p_user_id: userId })
    .throwOnError()
  return data
}

export async function likeQuote(userId: string, quoteId: string): Promise<void> {
  await supabase
    .from('quote_likes')
    .insert({ user_id: userId, quote_id: quoteId })
    .throwOnError()
}

export async function unlikeQuote(userId: string, quoteId: string): Promise<void> {
  await supabase
    .from('quote_likes')
    .delete()
    .match({ user_id: userId, quote_id: quoteId })
    .throwOnError()
}

// ── FEEDBACK ────────────────────────────────────────────────

export async function fetchDailyFeedback(userId: string, date?: string): Promise<unknown> {
  const params: Record<string, unknown> = { p_user_id: userId }
  if (date) params.p_date = date

  const { data } = await supabase
    .rpc('get_daily_feedback', params)
    .throwOnError()
  return data
}

// ── HABIT LIMIT CHECK ──────────────────────────────────────

export interface HabitLimitInfo {
  is_premium: boolean
  personal_count: number
  challenge_count: number
  total_count: number
  max_personal: number
  can_create: boolean
}

export async function checkHabitLimit(userId: string): Promise<HabitLimitInfo> {
  const { data } = await supabase
    .rpc('check_habit_limit', { p_user_id: userId })
    .throwOnError()
  return data as HabitLimitInfo
}

// ── CATEGORIES ──────────────────────────────────────────────

export async function fetchCategories(): Promise<unknown[]> {
  const { data } = await supabase
    .from('categories')
    .select('id, name_uz, name_en, name_ru, icon, sort_order')
    .order('sort_order')
    .throwOnError()
  return data ?? []
}

// ── TASK BOARDS ─────────────────────────────────────────────

export async function fetchTaskBoards(userId: string): Promise<unknown[]> {
  const { data } = await supabase
    .from('task_boards')
    .select('*, items:task_items(*)')
    .eq('user_id', userId)
    .order('sort_order')
    .throwOnError()
  return data ?? []
}

export async function createTaskBoard(userId: string, name: string, sortOrder: number): Promise<unknown> {
  const { data } = await supabase
    .from('task_boards')
    .insert({ user_id: userId, name, sort_order: sortOrder })
    .select()
    .single()
    .throwOnError()
  return data
}

export async function updateTaskBoard(boardId: string, name: string): Promise<void> {
  await supabase
    .from('task_boards')
    .update({ name })
    .eq('id', boardId)
    .throwOnError()
}

export async function deleteTaskBoard(boardId: string): Promise<void> {
  await supabase
    .from('task_boards')
    .delete()
    .eq('id', boardId)
    .throwOnError()
}

export async function createTaskItem(boardId: string, userId: string, text: string, sortOrder: number): Promise<unknown> {
  const { data } = await supabase
    .from('task_items')
    .insert({ board_id: boardId, user_id: userId, text, sort_order: sortOrder })
    .select()
    .single()
    .throwOnError()
  return data
}

export async function toggleTaskItem(itemId: string, completed: boolean): Promise<void> {
  const updates: Record<string, unknown> = { completed }
  if (completed) {
    updates.completed_at = new Date().toISOString()
  } else {
    updates.completed_at = null
  }

  await supabase
    .from('task_items')
    .update(updates)
    .eq('id', itemId)
    .throwOnError()
}

export async function deleteTaskItem(itemId: string): Promise<void> {
  await supabase
    .from('task_items')
    .delete()
    .eq('id', itemId)
    .throwOnError()
}

export async function clearCompletedItems(boardId: string): Promise<void> {
  await supabase
    .from('task_items')
    .delete()
    .eq('board_id', boardId)
    .eq('completed', true)
    .throwOnError()
}
